/**
 * OtherMeetingDebriefView - Renders customer operational meeting debrief
 */

import React, { useState } from 'react';
import { Text, Flex, Icon, ButtonSelect } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import type { OtherMeetingDebrief, BlockerRisk, GapOpportunity } from 'App/resources/Model/MeetingDebrief';
import {
	parseDebrief,
	formatStatus,
	SummaryCard,
	ActionItemList,
	DiscussedSection,
	StatusBadge,
	BulletPoint
} from './MeetingDebriefCommon';
import './OtherMeetingDebriefView.scss';

interface OtherMeetingDebriefViewProps {
	debrief: OtherMeetingDebrief | string | null;
	t: (text: string) => string;
	hideTitle?: boolean;
}

export function OtherMeetingDebriefView({ debrief: rawDebrief, t, hideTitle }: OtherMeetingDebriefViewProps) {
	const classes = new BemClass('MeetingDebriefView');
	const [isHealth2Expanded, setIsHealth2Expanded] = useState(true);
	const [isDiscussedExpanded, setIsDiscussedExpanded] = useState(true);
	const [isNextStepsExpanded, setIsNextStepsExpanded] = useState(true);

	const debrief = parseDebrief<OtherMeetingDebrief>(rawDebrief);
	const hasBlockers = debrief?.blockers_and_risks && debrief.blockers_and_risks.length > 0;
	const hasOpportunities = debrief?.gaps_and_opportunities && debrief.gaps_and_opportunities.length > 0;
	const [activeTab, setActiveTab] = useState<'blockers' | 'opportunities'>(() => {
		if (hasBlockers) return 'blockers';
		if (hasOpportunities) return 'opportunities';
		return 'blockers';
	});

	const options = [
		{
			value: 'blockers' as const,
			title: t('meetingDebrief.blockersRisks')
		},
		{
			value: 'opportunities' as const,
			title: t('meetingDebrief.gapsOpportunities')
		}
	];

	if (!debrief) {
		return (
			<Flex direction="column" gap="u6" spacing="pl4 pr4 pb4 pt4" className={classes.elem('container').b()}>
				<Text>{t('meetingDebrief.noSummaryAvailable')}</Text>
			</Flex>
		);
	}

	const healthStatusModifier =
		debrief.customer_health?.status === 'healthy'
			? 'healthy'
			: debrief.customer_health?.status === 'at-risk'
			? 'at-risk'
			: 'critical';

	return (
		<Flex direction="column" gap="u6" spacing="pl4 pr4 pb4 pt4" className={classes.elem('container').b()}>
			<SummaryCard summary={debrief.summary} classes={classes} t={t} hideTitle={hideTitle} />

			{debrief.customer_health ? (
				<Flex direction="column" gap="u3">
					<Flex
						alignItems="center"
						justifyContent="space-between"
						className={classes.elem('healthHeader').b()}
						onClick={() => setIsHealth2Expanded(!isHealth2Expanded)}
					>
						<Text size="lg" bold>
							{t('meetingDebrief.customerHealth')}
						</Text>
						<Text size="lg">
							<Icon
								name="caret-down"
								color="neutral-600"
								className={classes
									.elem('healthCaret')
									.mod(isHealth2Expanded ? 'expanded' : 'collapsed')
									.b()}
							/>
						</Text>
					</Flex>
					<div
						className={classes
							.elem('healthContent')
							.mod(isHealth2Expanded ? 'expanded' : 'collapsed')
							.b()}
					>
						<Flex
							direction="column"
							gap="u3"
							alignItems="flex-start"
							spacing="p4"
							className={classes.elem('healthStatusContainer').mod(healthStatusModifier).b()}
						>
							<Text
								size="sm"
								bold
								spacing="mb2"
								color={
									debrief.customer_health.status === 'healthy'
										? 'success-600'
										: debrief.customer_health.status === 'at-risk'
										? 'alert-600'
										: 'danger-600'
								}
							>
								{formatStatus(debrief.customer_health.status).toUpperCase()}
							</Text>
							{debrief.customer_health.signals?.map((signal, idx) => (
								<BulletPoint key={`signal-2-${idx}`} className={classes.elem('healthBullet').b()}>
									{signal}
								</BulletPoint>
							))}
						</Flex>
					</div>
				</Flex>
			) : null}

			{debrief.discussed && debrief.discussed.length > 0 ? (
				<Flex direction="column" gap="u4">
					<Flex
						justifyContent="space-between"
						alignItems="center"
						className={classes.elem('discussedHeader').b()}
						onClick={() => setIsDiscussedExpanded(!isDiscussedExpanded)}
					>
						<Flex gap="u2" alignItems="center">
							<Text size="lg" bold color="black">
								{t('meetingDebrief.whatWeDiscussed')}
							</Text>
							{debrief.date && (
								<Text size="sm" color="grey-11">
									{'• '}
									{new Date(debrief.date).toLocaleDateString('en-GB', {
										day: 'numeric',
										month: 'short',
										year: 'numeric'
									})}
								</Text>
							)}
						</Flex>
						<Icon
							name="chevron-down"
							className={classes
								.elem('discussedCaret')
								.mod(isDiscussedExpanded ? 'expanded' : 'collapsed')
								.b()}
						/>
					</Flex>
					<div
						className={classes
							.elem('discussedContent')
							.mod(isDiscussedExpanded ? 'expanded' : 'collapsed')
							.b()}
					>
						<div className={classes.elem('discussedSection').b()}>
							<Flex direction="column" gap="u3" spacing="p4">
								<DiscussedSection topics={debrief.discussed} classes={classes} />

								{hasBlockers || hasOpportunities ? (
									<>
										{hasBlockers && hasOpportunities ? (
											<Flex spacing="mt4">
												<ButtonSelect
													size="sm"
													value={activeTab}
													onChange={(value: 'blockers' | 'opportunities') =>
														setActiveTab(value)
													}
													className={classes.elem('buttonSelect').b()}
													options={options}
												/>
											</Flex>
										) : (
											<Flex justifyContent="center" alignItems="center" spacing="mt3">
												<Text size="lg" color="neutral-600" bold>
													{hasBlockers
														? t('meetingDebrief.blockersRisks')
														: t('meetingDebrief.gapsOpportunities')}
												</Text>
											</Flex>
										)}
										<Flex direction="column" spacing="pt4">
											{activeTab === 'blockers' ? (
												<Flex direction="column" gap="u3">
													{debrief.blockers_and_risks!.map(
														(blocker: BlockerRisk, idx: number) => (
															<Flex
																key={`blocker-${idx}`}
																gap="u3"
																alignItems="center"
																justifyContent="space-between"
															>
																<Flex gap="u3" alignItems="center">
																	<Text>
																		<Icon name="ban" color="danger-500" />
																	</Text>
																	<Flex direction="column" gap="u1">
																		<Text size="md">{blocker.issue}</Text>
																		<Text color="neutral-600" size="sm">
																			{t('meetingDebrief.owner')}
																			{': '}
																			{blocker.owner}
																			{' • '}
																			{formatStatus(blocker.status)}
																		</Text>
																	</Flex>
																</Flex>
																<StatusBadge
																	variant="severity"
																	value={blocker.severity}
																	t={t}
																/>
															</Flex>
														)
													)}
												</Flex>
											) : null}

											{activeTab === 'opportunities' ? (
												<Flex direction="column" gap="u3">
													{debrief.gaps_and_opportunities!.map(
														(gap: GapOpportunity, idx: number) => (
															<Flex key={`gap-${idx}`} gap="u3" alignItems="center">
																<Text>
																	<Icon name="lightbulb" color="success-500" />
																</Text>
																<Flex direction="column" gap="u1">
																	<Text size="md">{gap.opportunity}</Text>
																	<Text color="neutral-600" size="sm">
																		{gap.observation}
																	</Text>
																</Flex>
															</Flex>
														)
													)}
												</Flex>
											) : null}
										</Flex>
									</>
								) : null}
							</Flex>
						</div>
					</div>
				</Flex>
			) : null}

			{debrief.actions && debrief.actions.length > 0 && (
				<Flex direction="column" gap="u3">
					<Flex
						alignItems="center"
						justifyContent="space-between"
						className={classes.elem('nextStepsHeader').b()}
						onClick={() => setIsNextStepsExpanded(!isNextStepsExpanded)}
					>
						<Text size="lg" bold>
							{t('meetingDebrief.actionItems')}
						</Text>
						<Text size="lg">
							<Icon
								name="caret-down"
								color="neutral-600"
								className={classes
									.elem('nextStepsCaret')
									.mod(isNextStepsExpanded ? 'expanded' : 'collapsed')
									.b()}
							/>
						</Text>
					</Flex>
					<div
						className={classes
							.elem('nextStepsContent')
							.mod(isNextStepsExpanded ? 'expanded' : 'collapsed')
							.b()}
					>
						<div className={classes.elem('nextStepsSection').b()}>
							<ActionItemList actions={debrief.actions} classes={classes} />
						</div>
					</div>
				</Flex>
			)}
		</Flex>
	);
}
